/**
 * Resource Allocation Help Content
 *
 * Covers allocation grid, capacity planning, availability,
 * project view, and user view.
 */

import type { HelpContent } from '../types';
import {
  createHelpContent,
  createSection,
  createFAQ,
  createTip,
  createWalkthrough,
  createTroubleshooting,
  createRelatedLink,
} from '../utils';

// =============================================================================
// MAIN RESOURCES HELP
// =============================================================================

export const resourcesHelp: HelpContent = createHelpContent({
  id: 'resources',
  title: 'Resource Allocation',
  description: 'Team capacity planning, project allocations, and availability management',
  lastUpdated: '2026-02-08',
  version: '1.0',

  tips: [
    createTip(
      'tip-weekly',
      'pro-tip',
      'Weekly vs Monthly View',
      'Use the weekly view for short-term sprint planning. Switch to monthly for longer-term capacity forecasting and allocation adjustments.'
    ),
    createTip(
      'tip-over-allocation',
      'warning',
      'Over-Allocation',
      'If a team member\'s total allocation exceeds 100%, they are over-allocated. This may indicate conflicting project commitments that need resolution.'
    ),
  ],

  sections: [
    createSection(
      'overview',
      'Overview',
      `<p>The Resource Allocation system helps you plan and manage team capacity across projects. Key features include:</p>
      <ul>
        <li><strong>Allocation Grid</strong> — Visual grid showing who is allocated where and by how much</li>
        <li><strong>Week Navigator</strong> — Navigate forward and backward through weeks</li>
        <li><strong>Capacity Planning</strong> — View total vs available vs allocated capacity</li>
        <li><strong>Project View</strong> — See all resources allocated to a specific project</li>
        <li><strong>User View</strong> — See all project allocations for a specific person</li>
        <li><strong>Availability View</strong> — Find who has unallocated capacity</li>
      </ul>`
    ),
    createSection(
      'allocation-grid',
      'Allocation Grid',
      `<p>The allocation grid is the primary view showing:</p>
      <ul>
        <li><strong>Rows</strong> — Team members</li>
        <li><strong>Columns</strong> — Weeks (or months depending on view)</li>
        <li><strong>Cells</strong> — Allocation percentage per person per week per project</li>
        <li><strong>Color coding</strong> — Green (under-allocated), Yellow (fully allocated), Red (over-allocated)</li>
      </ul>
      <p>Click on a cell to edit the allocation. Changes save automatically.</p>`
    ),
    createSection(
      'week-navigator',
      'Week Navigator',
      '<p>Use the week navigator at the top to move through time. Arrow buttons advance or go back one week at a time. You can also jump to a specific date. The current week is highlighted.</p>'
    ),
    createSection(
      'capacity',
      'Capacity Planning',
      `<p>Capacity is calculated as:</p>
      <ul>
        <li><strong>Total Capacity</strong> = Standard work hours × Working days in period</li>
        <li><strong>Available Capacity</strong> = Total − Leave − Public Holidays</li>
        <li><strong>Allocated Capacity</strong> = Sum of all project allocations</li>
        <li><strong>Unallocated</strong> = Available − Allocated</li>
      </ul>
      <p>A healthy team typically has 5-10% unallocated capacity for ad-hoc work and meetings.</p>`
    ),
    createSection(
      'filters',
      'Department Filters',
      '<p>Filter the resource view by department to focus on your team. Managers see their direct reports by default. Admins can view all departments.</p>'
    ),
    createSection(
      'project-view',
      'Project View',
      '<p>The project view shows all team members allocated to a specific project. See who is working on the project, their allocation percentage, and the duration of their assignment. Useful for project managers to assess resourcing.</p>'
    ),
    createSection(
      'user-view',
      'User View',
      '<p>The user view shows all project allocations for a single person. See how their time is distributed across projects, identify over-allocation, and plan future assignments.</p>'
    ),
    createSection(
      'availability',
      'Availability View',
      '<p>The availability view highlights team members with unallocated capacity. Use this when you need to staff a new project or find backup for an absent team member.</p>'
    ),
  ],

  walkthroughs: [
    createWalkthrough(
      'walkthrough-allocate',
      'How to Allocate a Team Member to a Project',
      'Assign a team member to work on a project.',
      [
        {
          title: 'Navigate to Resources',
          description: 'Go to the Resources page from the sidebar menu.',
        },
        {
          title: 'Find the team member',
          description: 'Use the department filter or search to find the person you want to allocate.',
        },
        {
          title: 'Select the project',
          description: 'Click on the allocation cell for the desired week and project.',
        },
        {
          title: 'Set the percentage',
          description: 'Enter the allocation percentage (e.g., 50% means half their time). Check that total allocation doesn\'t exceed 100%.',
        },
        {
          title: 'Save and verify',
          description: 'The allocation saves automatically. Verify the person isn\'t over-allocated by checking the color coding.',
        },
      ],
      3
    ),
    createWalkthrough(
      'walkthrough-availability',
      'How to Check Team Availability',
      'Find who has capacity for new work.',
      [
        {
          title: 'Go to Availability view',
          description: 'Navigate to Resources > Availability.',
        },
        {
          title: 'Set the date range',
          description: 'Use the week navigator to set the period you need coverage for.',
        },
        {
          title: 'Filter by department',
          description: 'Narrow to the relevant department or skill set.',
        },
        {
          title: 'Review available capacity',
          description: 'Look for team members with green indicators (under-allocated). Their unallocated percentage shows how much capacity they have.',
        },
      ],
      2
    ),
  ],

  troubleshooting: [
    createTroubleshooting(
      'trouble-over-allocation',
      'Team member shows over 100% allocation',
      [
        'Multiple projects allocated more than the total available capacity',
        'Allocation not adjusted after project scope change',
        'Leave not accounted for in the allocation',
      ],
      [
        {
          description: 'Review project allocations',
          steps: [
            'Open the user view for the over-allocated person',
            'Identify which projects are contributing to the over-allocation',
            'Adjust allocations or discuss with project managers about priorities',
          ],
        },
      ]
    ),
    createTroubleshooting(
      'trouble-capacity-mismatch',
      'Capacity data doesn\'t match expectations',
      [
        'Leave not yet approved or not reflected',
        'Part-time schedule not configured',
        'Holiday calendar not assigned',
      ],
      [
        {
          description: 'Check user configuration',
          steps: [
            'Verify the user has the correct holiday calendar assigned (Admin settings)',
            'Check if part-time hours are configured if applicable',
            'Ensure approved leave is reflected in the system',
          ],
        },
      ]
    ),
    createTroubleshooting(
      'trouble-allocation-vs-actual',
      'Allocation doesn\'t match actual effort (timesheet)',
      [
        'Allocation is a plan, timesheet is actual — they naturally differ',
        'Team member worked on unallocated projects',
        'Allocation not updated after priorities shifted',
      ],
      [
        {
          description: 'Understand the difference',
          steps: [
            'Resource allocation is a forward-looking plan',
            'Timesheets capture actual hours worked',
            'Some variance is expected — use effort variance analytics to track the gap',
            'Update allocations regularly to keep them aligned with reality',
          ],
        },
      ]
    ),
  ],

  faqs: [
    createFAQ(
      'faq-percentage',
      'What does allocation percentage mean?',
      '100% means the person is working full-time on that project. 50% means half their time. Total allocation across all projects should ideally not exceed 100% (including buffer for meetings and admin).',
      ['percentage', 'meaning', 'what', '100%', 'allocation']
    ),
    createFAQ(
      'faq-capacity-hours',
      'How is capacity calculated?',
      'Capacity is based on standard working hours (e.g., 8h/day) minus leave and public holidays. Part-time schedules are factored in if configured for the user.',
      ['capacity', 'hours', 'calculation', 'formula']
    ),
    createFAQ(
      'faq-weekly-monthly',
      'Should I allocate weekly or monthly?',
      'Weekly allocation is more precise and better for sprint-based teams. Monthly allocation works well for longer-term projects. Choose based on how frequently your team\'s assignments change.',
      ['weekly', 'monthly', 'frequency', 'granularity']
    ),
    createFAQ(
      'faq-conflict',
      'How do I resolve allocation conflicts?',
      'When a person is over-allocated, review their project assignments. Discuss with project managers to prioritize and reduce allocations where possible. Consider splitting work or finding alternative resources.',
      ['conflict', 'over-allocated', 'resolve', 'priority']
    ),
    createFAQ(
      'faq-bulk-allocate',
      'Can I allocate multiple people at once?',
      'Currently, allocations are set per person per project per week. For large-scale planning, use the project view to see all allocated resources in one place.',
      ['bulk', 'multiple', 'batch', 'many']
    ),
    createFAQ(
      'faq-allocation-vs-effort',
      'Why doesn\'t allocation match the timesheet effort?',
      'Allocation is a plan; timesheets capture actual hours. Variance between planned and actual is normal. Use the Effort Variance analytics to track and improve estimation accuracy.',
      ['actual', 'effort', 'mismatch', 'variance']
    ),
  ],

  relatedLinks: [
    createRelatedLink('Projects', '/projects', { description: 'View project details and teams' }),
    createRelatedLink('Analytics', '/analytics', { description: 'Utilization and capacity analytics' }),
    createRelatedLink('Timesheet', '/timesheet', { description: 'Log actual hours worked' }),
  ],

  keywords: [
    'resources',
    'allocation',
    'capacity',
    'availability',
    'planning',
    'utilization',
    'team',
    'scheduling',
    'project staffing',
  ],
});

// =============================================================================
// SECTION-LEVEL HELP
// =============================================================================

export const resourcesAvailabilityHelp: HelpContent = createHelpContent({
  id: 'resources:availability',
  title: 'Resource Availability',
  description: 'Find team members with unallocated capacity',
  parentId: 'resources',
  sections: [
    createSection('overview', 'Overview', '<p>The availability view shows team members who have capacity for new work assignments. Filter by department, date range, and minimum available hours to find the right person.</p>'),
    createSection('usage', 'How to Use', '<p>Green indicators show available capacity. The percentage represents how much of their time is unallocated. Higher percentages mean more availability for new projects.</p>'),
  ],
  faqs: [
    createFAQ('faq-1', 'Does availability account for leave?', 'Yes, approved leave is automatically deducted from available capacity.', ['leave', 'deduct', 'account']),
  ],
  keywords: ['availability', 'capacity', 'free', 'unallocated'],
});

export const resourcesProjectsHelp: HelpContent = createHelpContent({
  id: 'resources:projects',
  title: 'Resources by Project',
  description: 'View all resources allocated to a specific project',
  parentId: 'resources',
  sections: [
    createSection('overview', 'Overview', '<p>See all team members working on a specific project, their allocation percentages, and the duration of their assignments. Useful for project managers to assess resourcing levels.</p>'),
  ],
  faqs: [
    createFAQ('faq-1', 'Can I adjust allocations from this view?', 'Yes, click on any allocation cell to modify it directly.', ['edit', 'adjust', 'change']),
  ],
  keywords: ['project', 'resources', 'team', 'allocation'],
});

export const resourcesUsersHelp: HelpContent = createHelpContent({
  id: 'resources:users',
  title: 'Resources by User',
  description: 'View all project allocations for a specific person',
  parentId: 'resources',
  sections: [
    createSection('overview', 'Overview', '<p>See how a specific person\'s time is distributed across projects. Identify over-allocation, plan future assignments, and understand their workload at a glance.</p>'),
  ],
  faqs: [
    createFAQ('faq-1', 'What does the total row show?', 'The total row sums all project allocations. If it exceeds 100%, the person is over-allocated.', ['total', 'sum', 'over']),
  ],
  keywords: ['user', 'person', 'workload', 'allocation'],
});
